import { View, StyleSheet, ScrollView } from 'react-native';
import { Icon, Text, theme } from '@socrait/react';

import { Loading } from '~/components/Loading';
import { type StudentDataLogEvent } from '~/services/studentData';

import { ErrorBox } from '~/components/ErrorBox';
import { getConciseDateTime } from '~/utils/date';

type DataLogPanelProps = {
  events: StudentDataLogEvent[] | undefined;
  counts: { warnings?: number; praises?: number; absences?: number } | undefined;
  isLoading: boolean;
  isError: boolean;
};

export function DataLogPanel({ events, counts, isLoading, isError }: DataLogPanelProps) {
  const behaviorEvents = events?.filter((e) => e.type === 'behavior');

  const hasCounts = counts && (counts.praises != null || counts.warnings != null);
  const countItems = hasCounts
    ? [
        counts.praises != null ? { icon: 'star' as const, value: counts.praises, label: 'PRAISES' } : null,
        counts.warnings != null ? { icon: 'hexagon' as const, value: counts.warnings, label: 'WARNINGS' } : null,
      ].filter(Boolean)
    : [];

  return (
    <View style={styles.container}>
      <Text style={styles.sectionLabel}>Data log</Text>
      <View style={styles.logBox}>
        {isLoading && <Loading backgroundColor="transparent" />}
        {isError && !isLoading && (
          <ErrorBox
            text="Failed to load data log. Please close this window and try again."
            containerStyle={{ borderRadius: 0 }}
          />
        )}
        {!isLoading && !isError && (
          <ScrollView contentContainerStyle={styles.scrollViewContent}>
            {countItems.length > 0 && (
              <View style={styles.totalsRow}>
                {countItems.map((item, index) => (
                  <View
                    key={item!.label}
                    style={[
                      styles.totalCountContainer,
                      index === countItems.length - 1 && styles.totalCountContainerLast,
                    ]}>
                    <Icon type={item!.icon} isSolid={true} />
                    <View style={styles.totalText}>
                      <Text style={styles.totalCount}>{item!.value}</Text>
                      <Text style={styles.totalLabel}>{item!.label}</Text>
                    </View>
                  </View>
                ))}
              </View>
            )}
            {behaviorEvents && behaviorEvents.length > 0 ? (
              behaviorEvents.map((event, index) => (
                <DataLogEntry key={index} event={event} isLast={index === behaviorEvents.length - 1} />
              ))
            ) : (
              <ErrorBox text="No entries found." containerStyle={{ borderRadius: 0 }} />
            )}
          </ScrollView>
        )}
      </View>
    </View>
  );
}

function DataLogEntry({ event, isLast }: { event: StudentDataLogEvent; isLast: boolean }) {
  return (
    <View style={[styles.entryRow, isLast && styles.entryRowLast]}>
      <Text style={styles.indicationText}>
        [{event.type.toUpperCase()}] {event.context}
      </Text>
      <View style={styles.entryContent}>
        <Text style={styles.timestampText}>{getConciseDateTime(new Date(event.timestamp))}</Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: 275,
    flexShrink: 0,
    flexDirection: 'column',
    gap: theme.spacing.xlarge,
  },
  sectionLabel: {
    fontSize: theme.fontSizes.small,
    fontWeight: theme.fontWeights.bold,
    height: 40, // to match the button height of the message panel
    display: 'flex',
    alignItems: 'center',
  },
  logBox: {
    flex: 1,
    borderWidth: 1,
    borderColor: theme.colors.gray,
    borderRadius: theme.borderRadius.large,
    backgroundColor: theme.colors.white,
    minHeight: 200,
    overflow: 'hidden',
  },
  scrollViewContent: {
    flex: 1,
  },
  totalsRow: {
    flexDirection: 'row',
  },
  totalCountContainer: {
    flex: 1,
    flexDirection: 'row',
    gap: theme.spacing.medium,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray,
    borderRightColor: theme.colors.gray,
    padding: theme.spacing.medium,
    justifyContent: 'center',
  },
  totalCountContainerLast: {
    borderRightWidth: 0,
  },
  totalText: {
    flexDirection: 'column',
    gap: theme.spacing.xsmall,
  },
  totalCount: {
    fontSize: theme.fontSizes.large,
    lineHeight: theme.fontSizes.large,
    fontWeight: theme.fontWeights.medium,
  },
  totalLabel: {
    fontSize: theme.fontSizes.xsmall,
  },
  entryRow: {
    flexDirection: 'column',
    gap: theme.spacing.medium,
    padding: theme.spacing.large,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray,
  },
  entryRowLast: {
    borderBottomWidth: 0,
  },
  indicationText: {
    fontSize: theme.fontSizes.small,
  },
  entryContent: {
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing.xsmall,
  },
  timestampText: {
    fontSize: theme.fontSizes.xsmall,
    color: theme.colors.darkGray,
  },
});
